import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Snackbar,
  Alert,
  LinearProgress,
  InputAdornment,
  Divider,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  CheckCircleOutline as ActiveIcon,
  WarningAmber as WarningIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import {
  useCustomers,
  useCreateCustomer,
  useUpdateCustomer,
  useDeleteCustomer,
  useCustomerStats,
  useOutstandingCustomers,
  useResendCustomerSms,
} from '../hooks/useCustomers';
import { Customer } from '../types';

function initials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

const AVATAR_COLORS = [
  '#ff9800',
  '#66bb6a',
  '#42a5f5',
  '#ab47bc',
  '#ef5350',
  '#26c6da',
  '#ffa726',
  '#8d6e63',
];
function avatarColor(name: string) {
  let n = 0;
  for (let i = 0; i < name.length; i++) n += name.charCodeAt(i);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}

const CustomersPage: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    gender: '' as '' | 'male' | 'female' | 'other',
    sms_opt_out: false,
  });

  const {
    data: customersData,
    isLoading,
    error,
    refetch,
    isFetching: customersFetching,
  } = useCustomers();
  const { data: stats, isFetching: statsFetching } = useCustomerStats();
  const { data: outstandingCustomers, isFetching: outstandingFetching } = useOutstandingCustomers();
  const [smsDialog, setSmsDialog] = useState<{
    open: boolean;
    customer: Customer | null;
    date: string;
  }>({
    open: false,
    customer: null,
    date: new Date().toISOString().split('T')[0],
  });

  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();
  const deleteMutation = useDeleteCustomer();
  const resendSmsMutation = useResendCustomerSms();

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (customer?: Customer) => {
    if (customer) {
      setSelectedCustomer(customer);
      setFormData({
        name: customer.name,
        phone: customer.phone_number || customer.phone || '',
        email: customer.email_address || customer.email || '',
        address: customer.address || '',
        notes: customer.notes || '',
        gender: customer.gender || '',
        sms_opt_out: customer.sms_opt_out ?? false,
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        notes: '',
        gender: '',
        sms_opt_out: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCustomer(null);
  };

  const handleResendSms = async () => {
    if (!smsDialog.customer) return;
    try {
      await resendSmsMutation.mutateAsync({ id: smsDialog.customer.id, date: smsDialog.date });
      showSnackbar(`SMS sent to ${smsDialog.customer.name}`);
      setSmsDialog({ open: false, customer: null, date: new Date().toISOString().split('T')[0] });
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message ?? 'Failed to send SMS', 'error');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Customer name is required', 'error');
      return;
    }
    try {
      const payload = { ...formData, gender: formData.gender || undefined };
      if (selectedCustomer) {
        await updateMutation.mutateAsync({ id: selectedCustomer.id, data: payload });
        showSnackbar('Customer updated');
      } else {
        await createMutation.mutateAsync(payload);
        showSnackbar('Customer created');
      }
      handleCloseDialog();
      refetch();
    } catch {
      showSnackbar('Failed to save customer', 'error');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (window.confirm(`Delete "${name}"?`)) {
      try {
        await deleteMutation.mutateAsync(id);
        showSnackbar('Customer deleted');
      } catch {
        showSnackbar('Failed to delete customer', 'error');
      }
    }
  };

  const rows = useMemo(() => {
    const all = customersData?.data || [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(
      (c: Customer) =>
        c.name.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.address?.toLowerCase().includes(q)
    );
  }, [customersData, search]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'name',
      headerName: 'Customer',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const color = avatarColor(params.value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.72rem',
                fontWeight: 700,
                bgcolor: `${color}22`,
                color,
                border: `1px solid ${color}44`,
                flexShrink: 0,
              }}
            >
              {initials(params.value)}
            </Avatar>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'phone_number',
      headerName: 'Phone',
      width: 160,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PhoneIcon sx={{ fontSize: 14, color: '#66bb6a', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              {params.value}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>
            —
          </Typography>
        ),
    },
    {
      field: 'gender',
      headerName: 'Gender',
      width: 90,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}
          >
            {params.value}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>
            —
          </Typography>
        ),
    },
    {
      field: 'created_at',
      headerName: 'Date Added',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
          {new Date(params.value).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 115,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row as Customer)}
            sx={{
              color: 'rgba(255,152,0,0.7)',
              '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.1)' },
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            title="Resend sale receipt SMS"
            disabled={
              !!(params.row as Customer).sms_opt_out || !(params.row as Customer).phone_number
            }
            onClick={() =>
              setSmsDialog({
                open: true,
                customer: params.row as Customer,
                date: new Date().toISOString().split('T')[0],
              })
            }
            sx={{
              color: 'rgba(33,150,243,0.7)',
              '&:hover': { color: '#2196f3', bgcolor: 'rgba(33,150,243,0.1)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
            }}
          >
            <SmsIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id, params.row.name)}
            sx={{
              color: 'rgba(244,67,54,0.6)',
              '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.1)' },
            }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <Typography variant="body1" sx={{ color: '#f44336', mb: 2 }}>
            Failed to load customers
          </Typography>
          <Button
            variant="outlined"
            onClick={() => refetch()}
            sx={{ borderColor: '#f44336', color: '#f44336' }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 2.5 }}>
      {(customersFetching || statsFetching || outstandingFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#ff9800,#ffa726)' },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 2 }}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
            Customers
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Manage customer records and contacts
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg,#ff9800,#ffa726)',
            color: '#fff',
            px: 2,
            '&:hover': { background: 'linear-gradient(135deg,#f57c00,#ff9800)' },
          }}
        >
          Add Customer
        </Button>
      </Box>

      {/* ── Stats strip ── */}
      {stats && (
        <Paper
          sx={{
            mb: 2,
            px: 0,
            py: 0,
            background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            border: '1px solid rgba(255,152,0,0.12)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {[
              {
                label: 'Total',
                value: stats.total_customers,
                icon: <GroupIcon sx={{ fontSize: 15 }} />,
                color: '#ff9800',
              },
              {
                label: 'Active',
                value: stats.active_customers,
                sub: `${stats.activity_rate?.toFixed(0)}%`,
                icon: <ActiveIcon sx={{ fontSize: 15 }} />,
                color: '#66bb6a',
              },
              {
                label: 'Inactive',
                value: stats.inactive_customers,
                icon: <PersonIcon sx={{ fontSize: 15 }} />,
                color: 'rgba(255,255,255,0.35)',
              },
              {
                label: 'Outstanding',
                value: stats.customers_with_outstanding,
                sub: outstandingCustomers
                  ? `GH₵ ${(outstandingCustomers.total_outstanding_amount / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                  : undefined,
                icon: <WarningIcon sx={{ fontSize: 15 }} />,
                color: '#ef5350',
              },
            ].map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <Box
                  sx={{
                    flex: 1,
                    py: 1.5,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '8px',
                      bgcolor: `${s.color}18`,
                      color: s.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', display: 'block' }}
                    >
                      {s.label.toUpperCase()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ color: s.color, lineHeight: 1.1 }}
                      >
                        {s.value}
                      </Typography>
                      {s.sub && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                        >
                          {s.sub}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                {i < arr.length - 1 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: 'rgba(255,152,0,0.08)' }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      )}

      {/* ── Table ── */}
      <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(255,152,0,0.15)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Table toolbar */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            borderBottom: '1px solid rgba(255,152,0,0.1)',
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}
          >
            Records
          </Typography>
          <Chip
            label={rows.length}
            size="small"
            sx={{
              bgcolor: 'rgba(255,152,0,0.1)',
              color: '#ff9800',
              fontWeight: 700,
              height: 18,
              fontSize: '0.7rem',
            }}
          />
          <Box flex={1} />
          <TextField
            size="small"
            placeholder="Search customers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': {
                height: 32,
                fontSize: '0.8rem',
                bgcolor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(255,152,0,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#ff9800' },
              },
              '& input': { color: '#fff', py: 0.5 },
              '& input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
            }}
          />
        </Box>

        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
          rowHeight={44}
          sx={{
            flex: 1,
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              color: '#fff',
              fontSize: '0.82rem',
            },
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'rgba(255,152,0,0.07)',
              borderBottom: '1px solid rgba(255,152,0,0.15)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.7px',
              color: 'rgba(255,152,0,0.7)',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(255,152,0,0.05)' },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(255,152,0,0.12)',
              bgcolor: 'rgba(255,152,0,0.03)',
              minHeight: 40,
            },
            '& .MuiTablePagination-root': { color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' },
            '& .MuiTablePagination-selectIcon': { color: 'rgba(255,255,255,0.4)' },
          }}
        />
      </Paper>

      {/* ── Add / Edit Dialog ── */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              border: '1px solid rgba(255,152,0,0.25)',
              background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            borderBottom: '1px solid rgba(255,152,0,0.15)',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#ff9800',
          }}
        >
          {selectedCustomer ? 'Edit Customer' : 'New Customer'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Name *"
                name="name"
                size="small"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                size="small"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                size="small"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                size="small"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                size="small"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Gender"
                size="small"
                value={formData.gender}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    gender: e.target.value as '' | 'male' | 'female' | 'other',
                  })
                }
              >
                <MenuItem value="">— Not specified —</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: formData.sms_opt_out
                    ? 'rgba(244,67,54,0.25)'
                    : 'rgba(102,187,106,0.25)',
                  bgcolor: formData.sms_opt_out ? 'rgba(244,67,54,0.05)' : 'rgba(102,187,106,0.05)',
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e0e0e0' }}>
                    SMS notifications
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {formData.sms_opt_out
                      ? 'Customer will NOT receive SMS receipts'
                      : 'Customer will receive SMS on each sale'}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!formData.sms_opt_out}
                      onChange={(e) => setFormData({ ...formData, sms_opt_out: !e.target.checked })}
                      color="success"
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid rgba(255,152,0,0.12)' }}>
          <Button size="small" onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg,#ff9800,#ffa726)',
              color: '#fff',
              px: 2.5,
              '&:hover': { background: 'linear-gradient(135deg,#f57c00,#ff9800)' },
              '&:disabled': { background: 'rgba(255,152,0,0.25)' },
            }}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : selectedCustomer ? (
              'Save'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Resend SMS Dialog ── */}
      <Dialog
        open={smsDialog.open}
        onClose={() => setSmsDialog((s) => ({ ...s, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Resend Receipt SMS</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Send a sale receipt SMS to <strong>{smsDialog.customer?.name}</strong>
            {smsDialog.customer?.phone_number ? ` (${smsDialog.customer.phone_number})` : ''}.
          </Typography>
          <TextField
            label="Date"
            type="date"
            fullWidth
            size="small"
            value={smsDialog.date}
            onChange={(e) => setSmsDialog((s) => ({ ...s, date: e.target.value }))}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button size="small" onClick={() => setSmsDialog((s) => ({ ...s, open: false }))}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleResendSms}
            disabled={resendSmsMutation.isPending || !smsDialog.date}
            startIcon={resendSmsMutation.isPending ? <CircularProgress size={14} /> : <SmsIcon />}
          >
            {resendSmsMutation.isPending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersPage;
